// src/redux/services/groupApi.js
// ✅ FIXED & COMPLETE GROUP API

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseQuery = fetchBaseQuery({
  // ✅ REACT_APP_BACKEND_URL = https://qylmox.in/api (already has /api)
  baseUrl: process.env.REACT_APP_BACKEND_URL || 'http://localhost:7600/api',
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth?.token || localStorage.getItem('token');
    if (token) {
      headers.set('x-auth-token', token);
    }
    headers.set('Content-Type', 'application/json');
    return headers;
  },
});

export const groupApi = createApi({
  reducerPath: 'groupApi',
  baseQuery,
  tagTypes: ['Groups', 'GroupDetail', 'GroupDevices', 'GroupStats'],
  
  endpoints: (builder) => ({
    // ═══════════════════════════════════════════════════════════════════
    // GROUP CRUD
    // ═══════════════════════════════════════════════════════════════════
    
    // Get all groups with pagination & filters
    getAllGroups: builder.query({
      query: ({ page = 1, limit = 20, search, groupType, status, sortBy, sortOrder } = {}) => ({
        url: '/groups',
        params: { 
          page, limit, 
          ...(search && { search }),
          ...(groupType && { groupType }),
          ...(status && { status }),
          ...(sortBy && { sortBy }),
          ...(sortOrder && { sortOrder }),
        },
      }),
      providesTags: (result) =>
        result?.data
          ? [...result.data.map(({ _id }) => ({ type: 'Groups', id: _id })), { type: 'Groups', id: 'LIST' }]
          : [{ type: 'Groups', id: 'LIST' }],
    }),

    getAllGroupsUnpaged: builder.query({
      async queryFn(arg = {}, _api, _extra, fetchWithBQ) {
        const limit = Number(arg?.limit || 500);
        const baseParams = {
          ...(arg?.search ? { search: arg.search } : {}),
          ...(arg?.groupType ? { groupType: arg.groupType } : {}),
          ...(arg?.status ? { status: arg.status } : {}),
          ...(arg?.sortBy ? { sortBy: arg.sortBy } : {}),
          ...(arg?.sortOrder ? { sortOrder: arg.sortOrder } : {}),
        };

        let page = 1;
        let allRows = [];
        let hasMore = true;

        while (hasMore) {
          const response = await fetchWithBQ({
            url: "/groups",
            params: {
              ...baseParams,
              page,
              limit,
            },
          });

          if (response.error) {
            return { error: response.error };
          }

          const rows = Array.isArray(response.data?.data) ? response.data.data : [];
          allRows = allRows.concat(rows);

          const pagination = response.data?.pagination || {};
          hasMore = Boolean(pagination?.hasMore) && rows.length > 0;
          page += 1;

          if (!rows.length) {
            hasMore = false;
          }
        }

        return {
          data: {
            data: allRows,
          },
        };
      },
      providesTags: (result) =>
        result?.data
          ? [...result.data.map(({ _id }) => ({ type: 'Groups', id: _id })), { type: 'Groups', id: 'LIST' }]
          : [{ type: 'Groups', id: 'LIST' }],
    }),

    // Get single group by ID
    getGroupById: builder.query({
      query: (id) => `/groups/${id}`,
      providesTags: (result, error, id) => [{ type: 'GroupDetail', id }],
    }),

    // Create new group
    createGroup: builder.mutation({
      query: (groupData) => ({
        url: '/groups',
        method: 'POST',
        body: groupData,
      }),
      invalidatesTags: [{ type: 'Groups', id: 'LIST' }],
    }),

    // Update group
    updateGroup: builder.mutation({
      query: ({ id, ...groupData }) => ({
        url: `/groups/${id}`,
        method: 'PUT',
        body: groupData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Groups', id: 'LIST' },
        { type: 'GroupDetail', id },
      ],
    }),

    // Delete group
    deleteGroup: builder.mutation({
      query: ({ id, permanent = false }) => ({
        url: `/groups/${id}`,
        method: 'DELETE',
        params: { permanent },
      }),
      invalidatesTags: [{ type: 'Groups', id: 'LIST' }],
    }),

    // Restore deleted group
    restoreGroup: builder.mutation({
      query: (id) => ({
        url: `/groups/${id}/restore`,
        method: 'PUT',
      }),
      invalidatesTags: [{ type: 'Groups', id: 'LIST' }],
    }),

    // ═══════════════════════════════════════════════════════════════════
    // DEVICE MANAGEMENT
    // ═══════════════════════════════════════════════════════════════════

    // Add devices to group
    addDevicesToGroup: builder.mutation({
      query: ({ groupId, deviceIds }) => ({
        url: `/groups/${groupId}/devices`,
        method: 'POST',
        body: { deviceIds },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: 'Groups', id: 'LIST' },
        { type: 'GroupDetail', id: groupId },
        { type: 'GroupDevices', id: groupId },
      ],
    }),

    // Remove devices from group
    removeDevicesFromGroup: builder.mutation({
      query: ({ groupId, deviceIds }) => ({
        url: `/groups/${groupId}/devices`,
        method: 'DELETE',
        body: { deviceIds },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: 'Groups', id: 'LIST' },
        { type: 'GroupDetail', id: groupId },
        { type: 'GroupDevices', id: groupId },
      ],
    }),

    // Get all devices in group (including sub-groups)
    getGroupDevices: builder.query({
      query: ({ groupId, includeSubGroups = true }) => ({
        url: `/groups/${groupId}/all-devices`,
        params: { includeSubGroups },
      }),
      providesTags: (result, error, { groupId }) => [{ type: 'GroupDevices', id: groupId }],
    }),

    // Bulk assign devices
    bulkAssignDevices: builder.mutation({
      query: ({ groupId, deviceIds, removeFromOtherGroups = false }) => ({
        url: `/groups/${groupId}/bulk-assign`,
        method: 'POST',
        body: { deviceIds, removeFromOtherGroups },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: 'GroupDetail', id: groupId },
        { type: 'GroupDevices', id: groupId },
      ],
    }),

    // ═══════════════════════════════════════════════════════════════════
    // MANAGER MANAGEMENT
    // ═══════════════════════════════════════════════════════════════════

    // Add manager to group
    addManagerToGroup: builder.mutation({
      query: ({ groupId, userId, role = 'viewer' }) => ({
        url: `/groups/${groupId}/managers`,
        method: 'POST',
        body: { userId, role },
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: 'GroupDetail', id: groupId }],
    }),

    // Remove manager from group
    removeManagerFromGroup: builder.mutation({
      query: ({ groupId, userId }) => ({
        url: `/groups/${groupId}/managers/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: 'GroupDetail', id: groupId }],
    }),

    // ═══════════════════════════════════════════════════════════════════
    // VIEWER MANAGEMENT
    // ═══════════════════════════════════════════════════════════════════

    // Add viewer to group
    addViewerToGroup: builder.mutation({
      query: ({ groupId, userId }) => ({
        url: `/groups/${groupId}/viewers`,
        method: 'POST',
        body: { userId },
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: 'GroupDetail', id: groupId }],
    }),

    // Remove viewer from group
    removeViewerFromGroup: builder.mutation({
      query: ({ groupId, userId }) => ({
        url: `/groups/${groupId}/viewers/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: 'GroupDetail', id: groupId }],
    }),

    // ═══════════════════════════════════════════════════════════════════
    // HIERARCHY & STATISTICS
    // ═══════════════════════════════════════════════════════════════════

    // Get group hierarchy
    getGroupHierarchy: builder.query({
      query: (groupId) => `/groups/${groupId}/hierarchy`,
      providesTags: (result, error, groupId) => [{ type: 'GroupDetail', id: groupId }],
    }),

    // Get group statistics
    getGroupStatistics: builder.query({
      query: (groupId) => `/groups/${groupId}/statistics`,
      providesTags: (result, error, groupId) => [{ type: 'GroupStats', id: groupId }],
    }),

    // ═══════════════════════════════════════════════════════════════════
    // OWNERSHIP & MOVEMENT
    // ═══════════════════════════════════════════════════════════════════

    // Transfer ownership
    transferOwnership: builder.mutation({
      query: ({ groupId, newOwnerId }) => ({
        url: `/groups/${groupId}/transfer-ownership`,
        method: 'PUT',
        body: { newOwnerId },
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: 'GroupDetail', id: groupId }],
    }),

    // Move group to different parent
    moveGroup: builder.mutation({
      query: ({ groupId, newParentId }) => ({
        url: `/groups/${groupId}/move`,
        method: 'PUT',
        body: { newParentId },
      }),
      invalidatesTags: [{ type: 'Groups', id: 'LIST' }],
    }),
  }),
});

export const {
  // Group CRUD
  useGetAllGroupsQuery,
  useLazyGetAllGroupsQuery,
  useGetAllGroupsUnpagedQuery,
  useGetGroupByIdQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,
  useRestoreGroupMutation,
  
  // Device Management
  useAddDevicesToGroupMutation,
  useRemoveDevicesFromGroupMutation,
  useGetGroupDevicesQuery,
  useBulkAssignDevicesMutation,
  
  // Manager Management
  useAddManagerToGroupMutation,
  useRemoveManagerFromGroupMutation,
  
  // Viewer Management
  useAddViewerToGroupMutation,
  useRemoveViewerFromGroupMutation,
  
  // Hierarchy & Statistics
  useGetGroupHierarchyQuery,
  useGetGroupStatisticsQuery,
  
  // Ownership & Movement
  useTransferOwnershipMutation,
  useMoveGroupMutation,
} = groupApi;
